/** The itinerary record — one itinerary (`demo.Itinerary_Group` and its default itinerary,
 *  O10: one `itinerary:<group id>` entity), read on the records core (plan
 *  2026-10-04-console-activity-itinerary-records.md §5, §9.5, §10.3).
 *
 *  The VIEW half reads two things, each through TanStack Query:
 *
 *  - `GET /itineraries/{pk}/` — the detail (`itineraries.retrieve`), under the key the old
 *    editor and every itinerary write already use (`['itineraries', 'detail', id]`);
 *  - `GET /itineraries/{pk}/used_by/summary/` — the template tours and their upcoming
 *    departures (`itineraries.usedBySummary`). It touches the tour-side tables, so the page
 *    asks for it only once the fact band has been on screen (never with the detail); the
 *    server remembers it 10 minutes, and so does the client.
 *
 *  The EDIT half (§9.8) reads and writes four more, the itinerary changeset's:
 *
 *  - `GET /itineraries/edit/catalog/` — the static registry: every field the record shows,
 *    then a day's (`entity: day`), with its card, widget, choices, where it is stored and
 *    what a feed sync does to it; the effects, the placeholder guard, the re-key question and
 *    the `ConsoleCatalogEditEnabled` kill switch (`enabled`). Cached for an hour.
 *  - `GET /itineraries/{pk}/edit_context/` — the per-itinerary facts: `baselines` (every
 *    change's `from`: the itinerary's fields — `days` the day ids in order — and each day's on
 *    `day:<id>`), `blocked` (the placeholder's `name` / `published`), the caller's
 *    permissions, the kill switch, the feed verdicts (a field's, and each day's by its
 *    title), the app reach, the catalog itinerary (none yet, shared) and the placeholder.
 *  - `POST /itineraries/{pk}/changeset/preview/` — the plan (the normalised diff with each
 *    value's verdict, the open question, the conflicts, the effects — among them what a
 *    removed day takes with it, `legacy_day_rows`, and what it unlinks, `removed_day_unlinks`
 *    — the deep link queued after the commit, the warnings); writes nothing.
 *  - `POST /itineraries/{pk}/changeset/` — the apply, compare-and-set per field (`days`
 *    merges): a 409 rejects as `CatalogDriftError` (`gone: true` for a day deleted meanwhile),
 *    a 400 as `CatalogChangesetValidationError`; the answer re-seeds the detail and the edit
 *    context, and `created` maps each new day's temporary id to its real one.
 *
 *  **The days on the wire** (`toItineraryChangeset`, O7): the draft holds the membership —
 *  `days` on the itinerary, the day ids in order, a negative id for a day it adds — and each
 *  day's own fields on `day:<id>`. A REMOVED day (in `days`' `from`, not in its `to`) is sent
 *  as all six `ItineraryDayRecordFieldKey` fields set to null, each compare-and-set from the
 *  loaded value (edit_context's baselines), so a removal never deletes an edit made
 *  meanwhile; a new day's changes carry `from: null`, and a new day the draft no longer lists
 *  is simply dropped. The days are capped at 365.
 *
 *  Vocabulary from the spec: `ItineraryRecordFieldKey` (every key the record shows),
 *  `ItineraryRecordEditableFieldKey` (the editable subset), `ItineraryDayRecordFieldKey` (a
 *  day's keys, on `day:<id>`) and `ItineraryRecordSection` (the cards, in page order) — the
 *  registry's contract test pins to the same enums. */
import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
  type QueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import { useRef } from 'react';

import type { RecordApplyResult, RecordChangeset, RecordPreview } from '@/features/records/editor/types';

import { CatalogDriftError, rekeyOptions, toCatalogChangesetError } from './catalogRecord';
import { apiGet, apiPost } from './client';
import * as itinerariesApi from './itineraries';
import type { ItineraryDetail, ItineraryListItem, ItineraryUsedBySummary } from './itineraries';
import type { components } from './types';

// ---------------------------------------------------------------------------
// Vocabulary
// ---------------------------------------------------------------------------

/** Every field key the itinerary record shows, editable or not, in card order. */
export type ItineraryRecordFieldKey = components['schemas']['ItineraryRecordFieldKey'];

/** The keys an `itinerary:<group id>` change may carry. */
export type ItineraryRecordEditableFieldKey = components['schemas']['ItineraryRecordEditableFieldKey'];

/** A day's keys (`day:<id>`), every one editable. */
export type ItineraryDayRecordFieldKey = components['schemas']['ItineraryDayRecordFieldKey'];

/** The record's cards, in page order (ItineraryRecordSection). */
export const ITINERARY_RECORD_SECTIONS = [
  'catalog_entry',
  'app_button',
  'days',
  'content',
  'visibility',
  'access',
  'identity',
] as const satisfies readonly components['schemas']['ItineraryRecordSection'][];
export type ItineraryRecordSection = (typeof ITINERARY_RECORD_SECTIONS)[number];

// ---------------------------------------------------------------------------
// Query keys (all under `['itineraries']`, so an itinerary write refreshes them)
// ---------------------------------------------------------------------------

export const itineraryDetailKey = (id: number) => ['itineraries', 'detail', id] as const;
export const itineraryUsedBySummaryKey = (id: number) => ['itineraries', 'usedBySummary', id] as const;

/** The server remembers a summary 10 minutes (2 when the read was cancelled). */
const SUMMARY_STALE_MS = 10 * 60_000;

// ---------------------------------------------------------------------------
// Hooks
// ---------------------------------------------------------------------------

export function useItineraryDetail(
  id: number,
  opts: { retry?: (failures: number, error: unknown) => boolean } = {},
): UseQueryResult<ItineraryDetail, unknown> {
  return useQuery<ItineraryDetail, unknown>({
    queryKey: itineraryDetailKey(id),
    queryFn: () => itinerariesApi.retrieve(id),
    ...(opts.retry ? { retry: opts.retry } : {}),
  });
}

/** The used-by summary — `enabled` only once the fact band has been seen. An unavailable
 *  summary (the database cancelled the read) is an answer, not an error. */
export function useItineraryUsedBySummary(
  id: number,
  { enabled }: { enabled: boolean },
): UseQueryResult<ItineraryUsedBySummary, unknown> {
  return useQuery<ItineraryUsedBySummary, unknown>({
    queryKey: itineraryUsedBySummaryKey(id),
    queryFn: () => itinerariesApi.usedBySummary(id),
    enabled,
    staleTime: SUMMARY_STALE_MS,
    retry: 1,
  });
}

/** How many day rows the itinerary probably has, from an itineraries grid page already in the
 *  cache (`['itineraries', params]` — the grid the operator came from): what the record's
 *  loading Days card is sized from before its detail arrives. The row's `day_count` is the
 *  spans' total — the row count when no day spans more than one, more than it otherwise; no
 *  catalog itinerary has no days. Null when no cached page lists the itinerary. */
export function cachedItineraryDayCount(qc: QueryClient, id: number): number | null {
  for (const [, data] of qc.getQueriesData<unknown>({ queryKey: ['itineraries'] })) {
    const rows = (data as { results?: unknown } | undefined)?.results;
    if (!Array.isArray(rows)) continue;
    // A grid row (another list under `['itineraries']` may share the id: a gallery's images).
    const row = (rows as (Partial<ItineraryListItem> | null)[]).find(
      (r) => r?.id === id && typeof r.default_missing === 'boolean',
    );
    if (!row) continue;
    if (row.default_missing) return 0;
    if (typeof row.day_count === 'number') return row.day_count;
  }
  return null;
}

// ---------------------------------------------------------------------------
// EDIT half: the itinerary changeset (§9.8)
// ---------------------------------------------------------------------------

type Schemas = components['schemas'];

export type ItineraryRecordCatalog = Schemas['ItineraryRecordCatalog'];
export type ItineraryRecordCatalogField = Schemas['ItineraryRecordCatalogField'];
export type ItineraryRecordEditContext = Schemas['ItineraryRecordEditContext'];
export type ItineraryRecordChange = Schemas['ItineraryRecordChange'];
export type ItineraryRecordChangeset = Schemas['ItineraryRecordChangeset'];
export type ItineraryRecordPreview = Schemas['ItineraryRecordPreview'];
/** The apply's answer, its `detail` in the shape the record reads (`itineraries.retrieve`). */
export type ItineraryRecordApplyResult = Omit<Schemas['ItineraryRecordApplyResult'], 'detail'> & {
  detail: ItineraryDetail | null;
};

/** The change entities: the itinerary (`itinerary:<group id>`) and its days (`day:<id>`). */
export const ITINERARY_CHANGE_ENTITY = 'itinerary';
export const DAY_CHANGE_ENTITY = 'day';

/** The itinerary's day membership (the day ids in order). */
export const DAYS_FIELD = 'days';

/** A day's fields, in `ItineraryDayRecordFieldKey` order — a removed day sends every one. */
export const ITINERARY_DAY_FIELDS = [
  'title',
  'subtitle',
  'day_span',
  'image',
  'description',
  'footer',
] as const satisfies readonly ItineraryDayRecordFieldKey[];

/** The most days an itinerary holds (the changeset refuses more). */
export const ITINERARY_DAYS_CAP = 365;

/** The most days one day row covers (`day_span`, a PositiveSmallIntegerField: the changeset
 *  refuses less than 1 or more than this; the catalog names neither bound). */
export const ITINERARY_DAY_SPAN_MAX = 32767;

/** The record keys' stored values (edit_context `baselines`). */
export type ItineraryBaselines = Readonly<Record<string, Readonly<Record<string, unknown>>>>;

const idsOf = (v: unknown): number[] =>
  Array.isArray(v) ? v.filter((x): x is number => typeof x === 'number') : [];

/** The saved days a membership change removes: in its `from`, not in its `to`. */
export function removedDayIds(from: unknown, to: unknown): number[] {
  const kept = new Set(idsOf(to));
  return idsOf(from).filter((id) => id > 0 && !kept.has(id));
}

/** The records core's changeset as this endpoint takes it (the days protocol above): the
 *  itinerary's field changes; each day's changes — a new day's with `from: null`, a new day
 *  the membership no longer lists dropped; and for every saved day the membership removes,
 *  its six fields set to null, each from its loaded value in `baselines` (edit_context's).
 *  The description, and the re-key answer with the Custom ID it confirms (O11; null confirms a
 *  removal). */
export function toItineraryChangeset(
  body: RecordChangeset,
  baselines: ItineraryBaselines | null | undefined,
): ItineraryRecordChangeset {
  const days = body.changes.find((c) => c.entity === ITINERARY_CHANGE_ENTITY && c.field === DAYS_FIELD);
  const listed = days ? new Set(idsOf(days.to)) : null;
  const removed = days ? removedDayIds(days.from, days.to) : [];
  const gone = new Set(removed);
  const changes: ItineraryRecordChange[] = [];
  for (const c of body.changes) {
    if (!c.field) continue;
    if (c.entity === ITINERARY_CHANGE_ENTITY) {
      changes.push({ entity: ITINERARY_CHANGE_ENTITY, id: c.id, field: c.field as ItineraryRecordChange['field'], from: c.from, to: c.to });
    } else if (c.entity === DAY_CHANGE_ENTITY) {
      // A removed day is sent whole below; a new day must be listed (else it was dropped).
      if (gone.has(c.id)) continue;
      if (c.id < 0 && listed && !listed.has(c.id)) continue;
      if (c.id < 0 && !listed) continue;
      changes.push({
        entity: DAY_CHANGE_ENTITY,
        id: c.id,
        field: c.field as ItineraryRecordChange['field'],
        from: c.id < 0 ? null : c.from,
        to: c.to,
      });
    }
  }
  for (const id of removed) {
    const stored = baselines?.[`${DAY_CHANGE_ENTITY}:${id}`] ?? {};
    for (const field of ITINERARY_DAY_FIELDS) {
      changes.push({ entity: DAY_CHANGE_ENTITY, id, field, from: stored[field] ?? null, to: null });
    }
  }
  const out: ItineraryRecordChangeset = { changes };
  if (body.description) out.description = body.description;
  const options = rekeyOptions(body.options);
  if (options) out.options = options;
  return out;
}

/** The automatic effects whose label is their sentence alone — the app reach, and what the
 *  removed days take with them or unlink ("Removing day #12 “Bangkok” also deletes …"):
 *  their labels name the rows and the (bounded) counts, so the ledger adds no count of its
 *  own, and the warning that repeats the sentence word for word is left out. */
export const SENTENCE_EFFECTS: ReadonlySet<string> = new Set(['app_reach', 'legacy_day_rows', 'removed_day_unlinks']);

/** The catalog itinerary a save creates (O9, F42: an itinerary without one — the app shows
 *  "Coming soon"), named on its ledger line: what it is, and that it comes with its row in the
 *  itinerary's day plans (`Itineraries_By_Group`). */
export const DEFAULT_CREATED_EFFECT = 'default_itinerary_created';
export const DEFAULT_CREATED_KEYS = 'the day plan the app shows, with no days yet — listed in this itinerary’s day plans';

/** An itinerary plan as the ledger reads it. An itinerary fires no events; the deep link its
 *  save queues after the commit (a rename, or a save of an itinerary without one) is the
 *  save's own follow-up, as the activity's time zone is — Branch.io keeps the link in step
 *  with the name — so it asks no confirmation. A catalog itinerary the save creates is named
 *  ("Creates its catalog itinerary · the day plan the app shows, …"), never "1 record". */
export function toItineraryRecordPreview(p: ItineraryRecordPreview): RecordPreview {
  const sentences = new Set<string>();
  const effects = Object.fromEntries(
    Object.entries(p.effects).map(([code, e]) => {
      if (code === DEFAULT_CREATED_EFFECT) return [code, { ...e, count: 0, key_labels: [DEFAULT_CREATED_KEYS] }];
      if (!SENTENCE_EFFECTS.has(code)) return [code, e];
      sentences.add(e.label);
      return [code, { ...e, count: 0 }];
    }),
  );
  return {
    ...p,
    effects,
    events: [],
    external: [],
    follow_ups: p.external,
    warnings: p.warnings.filter((w) => !sentences.has(w)),
  };
}

/** An apply result as the ledger reads it (no events, no event tag); the new days' real ids
 *  (`created`) ride with it, so the ledger's SAVED re-keys the draft in the same step. */
export function toItineraryApplyResult(r: ItineraryRecordApplyResult): RecordApplyResult {
  const created = createdDayIds(r.created);
  return {
    audit_id: r.audit_id,
    audit_uuid: r.audit_uuid,
    event_tag: null,
    saved_ts: r.saved_ts,
    changes: r.changes,
    events: [],
    follow_ups: r.follow_ups,
    warnings: r.warnings,
    ...(created.size > 0
      ? { created: Object.fromEntries([...created].map(([temp, real]) => [`${DAY_CHANGE_ENTITY}:${temp}`, real])) }
      : {}),
  };
}

/** The new days a save created: temporary id → real id (`created`'s `"day:-1": 9123`). */
export function createdDayIds(created: Readonly<Record<string, number>> | null | undefined): Map<number, number> {
  const out = new Map<number, number>();
  for (const [key, real] of Object.entries(created ?? {})) {
    const m = /^day:(-\d+)$/.exec(key);
    if (m && typeof real === 'number') out.set(Number(m[1]), real);
  }
  return out;
}

// ---- calls -----------------------------------------------------------------

export function fetchItineraryEditCatalog(): Promise<ItineraryRecordCatalog> {
  return apiGet<ItineraryRecordCatalog>('/itineraries/edit/catalog/');
}

export function fetchItineraryEditContext(id: number, signal?: AbortSignal): Promise<ItineraryRecordEditContext> {
  return apiGet<ItineraryRecordEditContext>(`/itineraries/${id}/edit_context/`, signal ? { signal } : undefined);
}

export async function previewItineraryChangeset(
  id: number,
  body: ItineraryRecordChangeset,
  signal?: AbortSignal,
): Promise<ItineraryRecordPreview> {
  try {
    return await apiPost<ItineraryRecordPreview>(`/itineraries/${id}/changeset/preview/`, body, signal ? { signal } : undefined);
  } catch (e) {
    throw toCatalogChangesetError(e);
  }
}

export async function applyItineraryChangeset(id: number, body: ItineraryRecordChangeset): Promise<ItineraryRecordApplyResult> {
  try {
    return await apiPost<ItineraryRecordApplyResult>(`/itineraries/${id}/changeset/`, body);
  } catch (e) {
    throw toCatalogChangesetError(e);
  }
}

// ---- React Query -----------------------------------------------------------

/** The catalog changes only with a deploy. */
export const ITINERARY_EDIT_CATALOG_STALE_MS = 60 * 60_000;

export const itineraryRecordKeys = {
  catalog: () => ['itineraryRecord', 'catalog'] as const,
  editContext: (id: number) => ['itineraryRecord', 'editContext', id] as const,
  preview: (id: number, body: ItineraryRecordChangeset) => ['itineraryRecord', 'preview', id, body] as const,
};

export function useItineraryEditCatalog(): UseQueryResult<ItineraryRecordCatalog> {
  return useQuery({
    queryKey: itineraryRecordKeys.catalog(),
    queryFn: fetchItineraryEditCatalog,
    staleTime: ITINERARY_EDIT_CATALOG_STALE_MS,
  });
}

export function useItineraryEditContext(
  id: number,
  { enabled = true }: { enabled?: boolean } = {},
): UseQueryResult<ItineraryRecordEditContext> {
  return useQuery({
    queryKey: itineraryRecordKeys.editContext(id),
    queryFn: ({ signal }) => fetchItineraryEditContext(id, signal),
    enabled: enabled && Number.isFinite(id),
  });
}

/** The ledger's preview of a (debounced) compiled draft; `null` = nothing to preview —
 *  keyed by the wire body (a removed day's `from` values come from `baselines`), the
 *  previous plan kept on screen while the next loads. */
export function useItineraryChangesetPreview(
  id: number,
  body: RecordChangeset | null,
  baselines: ItineraryBaselines | null | undefined,
): UseQueryResult<RecordPreview> {
  const wire = body ? toItineraryChangeset(body, baselines) : { changes: [] };
  return useQuery({
    queryKey: itineraryRecordKeys.preview(id, wire),
    queryFn: ({ signal }) => previewItineraryChangeset(id, wire, signal),
    select: toItineraryRecordPreview,
    enabled: body !== null && wire.changes.length > 0 && Number.isFinite(id),
    staleTime: 0,
    gcTime: 60_000,
    retry: false,
    placeholderData: keepPreviousData,
  });
}

export interface ApplyItineraryOptions {
  /** The draft lets go of what the save stored and moves what stays of the new days onto
   *  their real ids (the ledger's `SAVED`, the result's `created`) — one step. */
  onSaved?: (result: RecordApplyResult) => void;
  /** Then: the new days' temporary ids → their real ids, for what follows them outside the
   *  draft (the open day), before the re-seed. */
  onCreated?: (created: Map<number, number>) => void;
  /** The baselines a removed day's six changes are compared with — read when Save runs. */
  baselines?: ItineraryBaselines | null;
}

/** Save. `onSaved` runs first (the draft re-keyed with it), then `onCreated` with the new
 *  days' real ids, then the edit context and the detail are re-seeded from the answer —
 *  refetched instead when the server could not re-read them — and the itineraries' other
 *  reads refresh (the grids, the gallery); never the used-by summary, which an edit cannot
 *  change. A 409 rejects with `CatalogDriftError` (the edit context is refetched for the
 *  rebase), a 400 / field-keyed 403 with `CatalogChangesetValidationError`. */
export function useApplyItineraryChangeset(
  id: number,
  { onSaved, onCreated, baselines }: ApplyItineraryOptions = {},
): UseMutationResult<ItineraryRecordApplyResult, unknown, RecordChangeset> {
  const qc = useQueryClient();
  const latest = useRef(baselines);
  latest.current = baselines;
  return useMutation({
    mutationFn: (body: RecordChangeset) => applyItineraryChangeset(id, toItineraryChangeset(body, latest.current)),
    onSuccess: (result) => {
      onSaved?.(toItineraryApplyResult(result));
      const created = createdDayIds(result.created);
      if (created.size > 0) onCreated?.(created);
      if (result.edit_context) qc.setQueryData(itineraryRecordKeys.editContext(id), result.edit_context);
      else void qc.invalidateQueries({ queryKey: itineraryRecordKeys.editContext(id) });
      if (result.detail) qc.setQueryData(itineraryDetailKey(id), result.detail);
      void qc.invalidateQueries({
        queryKey: ['itineraries'],
        predicate: ({ queryKey: [, kind, key] }) =>
          kind !== 'usedBySummary' && !(result.detail && kind === 'detail' && key === id),
      });
    },
    onError: (e) => {
      if (e instanceof CatalogDriftError) {
        void qc.invalidateQueries({ queryKey: itineraryRecordKeys.editContext(id) });
      }
    },
  });
}
